import { useCallback } from 'react'

import {
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  Tooltip,
} from '@mui/material'
import {
  IconBrowser,
  IconFileImport,
  IconPencil,
  IconSparkles,
} from '@tabler/icons-react'

import MetadataChip from 'src/components/Chips/MetadataChip/MetadataChip'
import ObjectChip from 'src/components/Chips/ObjectChip/ObjectChip'
import WorkspaceMemberChip from 'src/components/WorkspaceMemberChip/WorkspaceMemberChip'
import { dayjs } from 'src/lib/dayjs'
import { PropertySources } from 'src/lib/objects'

// Component to display the property history entries (updates)
const PropertyHistoryEntries = ({
  updates,
  propertyDefinition,
  workspaceId,
  onPropertyValueClick,
}) => {
  const handleValueClick = useCallback(
    (update, index) => {
      if (update.option) {
        // For option-based values, we store the option value in a different format
        const optionValue = [{ id: update.option.id, value: update.value }]
        onPropertyValueClick(
          update.objectId,
          optionValue,
          `update-option-${index}-${update.objectId}-${update.option.id}`
        )
      } else {
        onPropertyValueClick(
          update.objectId,
          update.value,
          `update-value-${index}`
        )
      }
    },
    [onPropertyValueClick]
  )

  if (!updates || updates.length === 0) {
    return (
      <Typography>No updates found for {propertyDefinition.name}</Typography>
    )
  }

  return (
    <Table>
      <TableBody>
        {updates.map((update, index) => (
          <Tooltip
            title={update.reasoning?.reasoning || update.reasoning}
            key={`update-${index}-${propertyDefinition.id}`}
            arrow={true}
            placement="left"
          >
            <TableRow className="property-update-row">
              <TableCell sx={{ width: '56px' }}>
                <Typography variant="caption">
                  {dayjs(update.createdAt).format('M/D/YY')}
                </Typography>
              </TableCell>

              <TableCell sx={{ width: '24px' }}>
                {update.propertySource === PropertySources.AIGenerated && (
                  <IconSparkles size={14} />
                )}
                {update.propertySource === PropertySources.ManualEntry && (
                  <IconPencil size={14} />
                )}
                {update.propertySource === PropertySources.Import && (
                  <IconFileImport size={14} />
                )}
                {update.propertySource === PropertySources.ParallelAi && (
                  <IconBrowser size={14} />
                )}
              </TableCell>
              <TableCell sx={{ width: '128px' }}>
                <ObjectChip
                  crmObject={{
                    objectId: update.objectId,
                    objectType: propertyDefinition.objectTypeId,
                    workspaceId,
                  }}
                  workspaceId={workspaceId}
                />
              </TableCell>
              {update.option ? (
                <TableCell
                  id={`update-option-${index}-${update.objectId}-${update.option.id}`}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleValueClick(update, index)}
                >
                  <MetadataChip
                    state={{
                      label: `${update.option.name} is "${update.value}"`,
                      value: update.option.id,
                      color: 'secondary',
                    }}
                  />
                </TableCell>
              ) : (
                <TableCell>
                  <Typography
                    className="property-update-value"
                    id={`update-value-${index}`}
                    onClick={() => handleValueClick(update, index)}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        textDecoration: 'underline',
                        color: 'primary.main',
                      },
                    }}
                  >
                    {update.value}
                  </Typography>
                </TableCell>
              )}
              <TableCell className="ellipsis">
                {update.userId ? (
                  <WorkspaceMemberChip
                    userId={update.userId}
                    workspaceId={workspaceId}
                  />
                ) : (
                  <>
                    {update.propertySource === PropertySources.ManualEntry ? (
                      <Typography className="property-update-reasoning">
                        {update.reasoning || 'Manually edited'}
                      </Typography>
                    ) : update.propertySource === PropertySources.Import ? (
                      <Typography className="property-update-reasoning">
                        {update.reasoning || 'Imported'}
                      </Typography>
                    ) : (
                      <Typography className="property-update-reasoning">
                        {update.reasoning || ''}
                      </Typography>
                    )}
                  </>
                )}
              </TableCell>
            </TableRow>
          </Tooltip>
        ))}
      </TableBody>
    </Table>
  )
}

export default PropertyHistoryEntries
